import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useGetAgentHisotryMutation,
  useGetAiWorkerByIdQuery,
  useTypedSelector,
} from '@circuitry-ai/doc-data';
import { Card, CardHeader, CardBody, CardFooter, Divider, Button } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { CtyButton, ErrorBoundary } from '@circuitry-ai/doc-ui';

interface FormUsageRecord {
  aiworker_usage_id: string;
  aiworker_id: string;
  title: string;
  aiworker_name: string;
  agent_name?: string;
  status: string;
  used_at: string;
  date: string;
  timestamp?: string;
  user_id: string;
  user_name: string;
  aiworker_type?: string;
  input_data?: { schema_input?: string; original_data?: string; user_input?: string };
  // Saved via output_data, returned by API as action_output
  output_data?: { generated_code?: string; form_config?: string; form_name?: string; action_name?: string; saved_at?: string };
  action_output?: { generated_code?: string; form_config?: string; form_name?: string; action_name?: string; saved_at?: string };
  created_at?: string;
}

const getOutputData = (record: FormUsageRecord) =>
  record.action_output || record.output_data;

const getFormTitle = (record: FormUsageRecord): string => {
  const out = getOutputData(record);
  return (
    record.title?.trim() ||
    out?.action_name ||
    out?.form_name ||
    record.agent_name ||
    record.aiworker_name ||
    'Untitled Form'
  );
};

const getRecordDate = (record: FormUsageRecord): Date => {
  const out = getOutputData(record);
  return new Date(record.date || record.used_at || record.timestamp || out?.saved_at || 0);
};

const formatVersionDatetime = (record: FormUsageRecord): string => {
  const d = getRecordDate(record);
  if (isNaN(d.getTime())) return 'N/A';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getStatusColor = (s: string) => {
  const sl = s?.toLowerCase() || '';
  if (sl.includes('succeed') || sl === 'completed') return 'bg-green-100 text-green-700';
  if (sl.includes('progress') || sl === 'processing') return 'bg-blue-100 text-blue-700';
  if (sl.includes('fail') || sl === 'error') return 'bg-red-100 text-red-700';
  return 'bg-default-100 text-default-700';
};

// --- Versioned Form Card ---
interface VersionedFormCardProps {
  versions: FormUsageRecord[]; // all versions for this form name, sorted newest first
  onView: (record: FormUsageRecord) => void;
  onEdit: (record: FormUsageRecord) => void;
  t: any;
}

const VersionedFormCard: React.FC<VersionedFormCardProps> = ({ versions, onView, onEdit, t }) => {
  const [selectedIdx, setSelectedIdx] = useState(0);
  const record = versions[selectedIdx];
  const title = getFormTitle(versions[0]);
  const status = record.status || 'unknown';

  return (
    <Card className="border shadow-sm w-full max-w-xs" shadow="none">
      <CardHeader className="flex justify-between items-start pb-2">
        <div className="flex flex-col gap-1 flex-1">
          <div className="flex items-center gap-2">
            <Icon icon="mdi:form-select" className="text-blue-600" width={20} />
            <span className="text-foreground font-semibold text-base">{title}</span>
          </div>
          <span className="text-xs text-default-400">
            {versions.length} version{versions.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${getStatusColor(status)}`}>
          <Icon icon="mdi:circle" width={8} />
          {status?.charAt(0).toUpperCase() + status?.slice(1).toLowerCase() || 'Unknown'}
        </div>
      </CardHeader>

      <CardBody className="pt-0 flex flex-col gap-3">
        <div className="flex items-center gap-2 text-xs text-default-500">
          <Icon icon="mdi:account" width={13} />
          <span>{record.user_name || record.user_id || 'N/A'}</span>
        </div>

        {/* Version selector — only show when there are multiple versions */}
        {versions.length > 1 && (
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-default-500">Version</span>
            <select
              value={selectedIdx}
              onChange={(e) => setSelectedIdx(Number(e.target.value))}
              className="text-sm border border-default-200 rounded-lg px-2 py-1.5 text-default-700 bg-content1 focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer"
            >
              {versions.map((v, idx) => (
                <option key={v.aiworker_usage_id} value={idx}>
                  {idx === 0 ? `Latest — ${formatVersionDatetime(v)}` : formatVersionDatetime(v)}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Single version — just show date */}
        {versions.length === 1 && (
          <span className="text-xs text-default-400">{formatVersionDatetime(versions[0])}</span>
        )}
      </CardBody>

      <Divider />

      <CardFooter className="flex gap-2">
        <CtyButton
          className="flex-1"
          variant="bordered"
          radius="sm"
          size="md"
          color="default"
          onPress={() => onView(record)}
        >
          <Icon icon="mdi:eye" width={16} className="mr-1" />
          {t('View')}
        </CtyButton>
        <CtyButton
          className="flex-1"
          variant="solid"
          radius="sm"
          size="md"
          color="primary"
          onPress={() => onEdit(record)}
        >
          <Icon icon="mdi:pencil" width={16} className="mr-1" />
          {t('Edit')}
        </CtyButton>
      </CardFooter>
    </Card>
  );
};

const VersionedFormCardWithErrorBoundary = ErrorBoundary(VersionedFormCard as unknown as React.ComponentType<Record<string, unknown>>);

export const FormGenieHistory: React.FC<{ aiworkerId?: string; basePath?: string }> = ({
  aiworkerId: propAiworkerId,
  basePath = '/formgenie',
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { aiworker_id: routeAiworkerId } = useParams<{ aiworker_id: string }>();
  const token = useTypedSelector((state) => state.auth.token);

  const aiworker_id = propAiworkerId || routeAiworkerId;

  const [records, setRecords] = useState<FormUsageRecord[]>([]);
  const { data: workerData, isLoading: isLoadingWorker } = useGetAiWorkerByIdQuery(aiworker_id || '', { skip: !aiworker_id });
  const [getUsageHistory, { isLoading: isLoadingHistory }] = useGetAgentHisotryMutation();

  useEffect(() => {
    if (!aiworker_id || !token) return;
    getUsageHistory({
      pagination: { page: 1, per_page: 50 },
      filters: [
        { aiworker_type: 'uxgenie_logs', condition: 'contains' },
        { aiworker_id: aiworker_id, condition: 'contains' },
      ],
    })
      .unwrap()
      .then((response) => {
        let data: FormUsageRecord[] = [];
        if (Array.isArray(response?.aiworkers_conversations)) data = response.aiworkers_conversations;
        else if (Array.isArray(response?.data?.aiworkers_conversations)) data = response.data.aiworkers_conversations;
        else if (Array.isArray(response?.data)) data = response.data;
        else if (Array.isArray(response)) data = response;
        setRecords(data);
      })
      .catch(() => setRecords([]));
  }, [aiworker_id, token, getUsageHistory]);

  // Group records by form title, sorted newest first within each group
  const groupedForms = useMemo(() => {
    const filtered = records.filter((r) => r.aiworker_id === aiworker_id);
    const groups: Record<string, FormUsageRecord[]> = {};
    for (const record of filtered) {
      const title = getFormTitle(record);
      if (!groups[title]) groups[title] = [];
      groups[title].push(record);
    }
    // Sort each group newest first
    for (const title of Object.keys(groups)) {
      groups[title].sort((a, b) => getRecordDate(b).getTime() - getRecordDate(a).getTime());
    }
    // Return as array of version arrays, sorted by the latest version date desc
    return Object.values(groups).sort(
      (a, b) => getRecordDate(b[0]).getTime() - getRecordDate(a[0]).getTime()
    );
  }, [records, aiworker_id]);

  const handleView = (record: FormUsageRecord) => {
    navigate(`${basePath}/${aiworker_id}/edit/${record.aiworker_usage_id}`, {
      state: { formGenieWorker: workerData, record, readOnly: true },
    });
  };

  const handleEdit = (record: FormUsageRecord) => {
    navigate(`${basePath}/${aiworker_id}/edit/${record.aiworker_usage_id}`, {
      state: { formGenieWorker: workerData, record, editMode: true },
    });
  };

  const handleCreate = () => {
    navigate(`${basePath}/${aiworker_id}/execute`, {
      state: { formGenieWorker: workerData },
    });
  };

  const isLoading = isLoadingWorker || isLoadingHistory;

  return (
    <div className="relative flex flex-col h-full gap-4 p-4">
      <div className="absolute top-4 right-4 z-10">
        <Button
          color="primary"
          variant="solid"
          size="sm"
          onPress={handleCreate}
          startContent={<Icon icon="fa6-solid:plus" width={15} />}
        >
          {t('Generate Form')}
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-3">
              <Icon icon="eos-icons:loading" className="text-blue-500" width={48} />
              <span className="text-default-500">{t('Loading forms...')}</span>
            </div>
          </div>
        ) : groupedForms.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full border-2 border-dashed border-default-300 rounded-lg m-4 bg-default-100 p-8">
            <Icon icon="mdi:form-select" className="text-default-400 mb-4" width={64} />
            <div className="text-xl text-default-500 font-medium mb-2">{t('No Forms Yet')}</div>
            <p className="text-sm text-default-500 text-center max-w-md">
              {t('No forms have been generated yet. Click "Generate Form" to create one from a Universal Schema.')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {groupedForms.map((versions) => (
              <VersionedFormCardWithErrorBoundary
                key={versions[0].aiworker_usage_id}
                versions={versions as any}
                onView={handleView as any}
                onEdit={handleEdit as any}
                t={t}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FormGenieHistory;
